import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import HttpService from "../services/HttpService";
import { useQuery } from "@tanstack/react-query";
import { JsonModal } from "./JsonViewer";
import { formatLocalDateTime, parseDatabaseDate } from "../util/dateTime";

const DOCUMENT_TYPE_LABELS = {
  PASSPORT: "Passport",
  DRIVERS_LICENSE: "Driver's License",
  COUNTRY_ID: "Country ID",
  PROOF_OF_ADDRESS: "Proof of Address",
  SSN_CARD: "SSN Card",
  ITIN_LETTER: "ITIN Letter",
};

const STATUS_COLOR = {
  UPLOADED: "success",
  PENDING_UPLOAD: "warning",
  REJECTED: "error",
  IDENTIFICATION_ONLY: "info",
};

const STATUS_LABEL = {
  UPLOADED: "Uploaded",
  PENDING_UPLOAD: "Pending Upload",
  REJECTED: "Rejected",
  IDENTIFICATION_ONLY: "ID Only",
};

function statusChip(status) {
  return (
    <Chip
      label={STATUS_LABEL[status] || status || "—"}
      color={STATUS_COLOR[status] || "default"}
      size="small"
      variant={status === "IDENTIFICATION_ONLY" ? "outlined" : "filled"}
    />
  );
}

export default function KycDocumentsViewer({
  memberId,
  companyId,
  expanded,
  onViewDocument,
}) {
  const {
    data: documents,
    isFetching,
    isError,
  } = useQuery(
    ["kycDocuments", memberId, companyId],
    async () => {
      const res = await HttpService.getAxiosClient().get(
        window.API_URL +
          `/member/kyc-documents?memberId=${memberId}&companyId=${companyId}`
      );
      const all = res.data || [];
      return all.filter((d) => d.status !== "IDENTIFICATION_ONLY");
    },
    {
      enabled: expanded && !!memberId && !!companyId,
      refetchOnWindowFocus: false,
    }
  );

  return (
    <>
      {isFetching && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {isError && !isFetching && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Failed to load KYC documents.
        </Alert>
      )}

      {!isFetching && !isError && documents && (
        <>
          {documents.length === 0 ? (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mt: 1, fontStyle: "italic" }}
            >
              No KYC documents found for this member.
            </Typography>
          ) : (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Document Type</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Side</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Country</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Status</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Uploaded</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Preview</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Image Details</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.id} hover>
                      <TableCell>
                        {DOCUMENT_TYPE_LABELS[doc.documentType] ||
                          doc.documentType}
                      </TableCell>
                      <TableCell>{doc.side}</TableCell>
                      <TableCell>{doc.countryCode || "—"}</TableCell>
                      <TableCell>{statusChip(doc.status)}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {formatLocalDateTime(
                          parseDatabaseDate(doc.uploadedAt),
                          true
                        ) || "—"}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "primary.main",
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                            onClick={() =>
                              onViewDocument && onViewDocument(doc, false)
                            }
                          >
                            View scaled
                          </Typography>
                          {doc.originalBlobPath && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: "primary.main",
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                              onClick={() =>
                                onViewDocument && onViewDocument(doc, true)
                              }
                            >
                              View original
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, whiteSpace: "pre-line" }}>
                        {doc.scaleDetails ? (
                          <JsonModal data={doc.scaleDetails}>
                            <Button size="small" variant="outlined">
                              View JSON
                            </Button>
                          </JsonModal>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}
    </>
  );
}
